// cspell:ignore Nominatim geodata Geodata
import { updateLocationWithStateAndRegion } from './location.lib';
import type { LocationData } from './location.lib';

export const BRAZIL_REGIONS: Record<string, string> = {
  // Norte (N)
  Acre: 'N',
  Amapá: 'N',
  Amazonas: 'N',
  Pará: 'N',
  Rondônia: 'N',
  Roraima: 'N',
  Tocantins: 'N',
  // Nordeste (NE)
  Alagoas: 'NE',
  Bahia: 'NE',
  Ceará: 'NE',
  Maranhão: 'NE',
  Paraíba: 'NE',
  Pernambuco: 'NE',
  Piauí: 'NE',
  'Rio Grande do Norte': 'NE',
  Sergipe: 'NE',
  // Centro-Oeste (CO)
  'Distrito Federal': 'CO',
  Goiás: 'CO',
  'Mato Grosso': 'CO',
  'Mato Grosso do Sul': 'CO',
  // Sudeste (SE)
  'Espírito Santo': 'SE',
  'Minas Gerais': 'SE',
  'Rio de Janeiro': 'SE',
  'São Paulo': 'SE',
  // Sul (S)
  Paraná: 'S',
  'Rio Grande do Sul': 'S',
  'Santa Catarina': 'S'
};

interface NominatimResponse {
  address?: {
    state?: string;
    state_district?: string;
    region?: string;
    country?: string;
  };
}

const logWarn = (...args: unknown[]) => {
  try {
    // eslint-disable-next-line no-console
    console.warn('[Geocoding]', ...args);
  } catch {}
};

const logInfo = (...args: unknown[]) => {
  try {
    // eslint-disable-next-line no-console
    console.log('[Geocoding]', ...args);
  } catch {}
};

const fetchNominatim = async (url: string): Promise<Response | null> => {
  try {
    const response = await fetch(url, {
      headers: {
        'Accept-Language': 'pt-BR,pt;q=0.9,en;q=0.8'
      }
    });

    if (response.ok) {
      return response;
    }

    return null;
  } catch (error) {
    logWarn('Attempts failed:', error);
    return null;
  }
};

const getStateFromNominatim = async (
  lat: number,
  lng: number
): Promise<string | null> => {
  const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=10&accept-language=pt-BR`;

  try {
    const response = await fetchNominatim(url);

    if (!response) {
      return null;
    }

    const data: NominatimResponse = await response.json();

    const state =
      data.address?.state ||
      data.address?.state_district ||
      data.address?.region;

    if (state) {
      const cleanState = state
        .replace(/^(estado de|região de|state of)\s+/i, '')
        .trim();

      logInfo(`Found state: ${cleanState}`);
      return cleanState;
    }

    return null;
  } catch (error) {
    logWarn('Failed to get state from Nominatim:', error);
    return null;
  }
};

export const getStateAndRegion = async (
  lat: number,
  lng: number
): Promise<{ state: string; region: string } | null> => {
  try {
    const state = await getStateFromNominatim(lat, lng);

    if (!state) {
      logWarn('Could not determine state from coordinates');
      return null;
    }

    const region = BRAZIL_REGIONS[state];

    if (!region) {
      logWarn(`Unknown Brazilian state: ${state}`);
      return null;
    }

    logInfo(`Resolved location: ${state} (${region})`);
    return { state, region };
  } catch (error) {
    logWarn('Failed to get state and region:', error);
    return null;
  }
};

export const enrichLocationWithGeodata = async (
  location: LocationData
): Promise<LocationData> => {
  if (location.state && location.region) {
    return location;
  }

  try {
    const geoData = await getStateAndRegion(
      location.latitude,
      location.longitude
    );

    if (geoData) {
      updateLocationWithStateAndRegion(location, geoData.state, geoData.region);

      return {
        ...location,
        state: geoData.state,
        region: geoData.region
      };
    }
  } catch (error) {
    logWarn('Failed to enrich location with geodata:', error);
  }

  return location;
};
